// [S-210] Overflow (⋯) menu for a Template Management row. Keeps one
// emphasized inline action (Open) in the row and collapses the rest —
// Flag for review, Export, Deprecate — behind this menu so a row never
// crowds up to five equal-weight buttons. Deprecate sits below a
// separator as a red `ds-menu__item--danger` so the irreversible action
// carries a visual warning. Mirrors DraftActionsMenu's pattern; lives in
// components/ so the kebab can be a raw <button> trigger (Radix asChild
// needs a real element to anchor to) without tripping the screens-only
// no-raw-<button> lint rule.
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../DropdownMenu";

export interface TemplateRowMenuProps {
  templateId: string;
  canTag: boolean;
  canFlag: boolean;
  canExport: boolean;
  canDeprecate: boolean;
  /** [S-718] L1-only, per-tenant opt-out (S-063b) — hides the template from
   *  this tenant's drafting agent + browse screens without deprecating it
   *  platform-wide. */
  canExclude: boolean;
  isExcluded: boolean;
  exporting: boolean;
  onEditTags: () => void;
  onFlag: () => void;
  onExport: () => void;
  onDeprecate: () => void;
  onExclude: () => void;
  onInclude: () => void;
}

export function TemplateRowMenu({
  templateId,
  canTag,
  canFlag,
  canExport,
  canDeprecate,
  canExclude,
  isExcluded,
  exporting,
  onEditTags,
  onFlag,
  onExport,
  onDeprecate,
  onExclude,
  onInclude,
}: TemplateRowMenuProps): JSX.Element {
  return (
    <DropdownMenu
      trigger={
        <button
          type="button"
          className="btn btn--g btn--icon"
          aria-label={`More actions for ${templateId}`}
          data-testid={`tplmgmt-menu-${templateId}`}
        >
          <KebabIcon />
        </button>
      }
    >
      {/* [S-216] Retag works in ANY lifecycle state — tags are metadata, so
          this is available from the list for drafts / deprecated / under-review
          rows that can't reach the certified-only detail page's tag editor. */}
      {canTag && (
        <DropdownMenuItem onSelect={() => onEditTags()}>
          <TagIcon />
          Edit tags
        </DropdownMenuItem>
      )}
      {canFlag && (
        <DropdownMenuItem onSelect={() => onFlag()}>
          <FlagIcon />
          Flag for review
        </DropdownMenuItem>
      )}
      {canExport && (
        <DropdownMenuItem disabled={exporting} onSelect={() => onExport()}>
          <ExportIcon />
          {exporting ? "Exporting…" : "Export"}
        </DropdownMenuItem>
      )}
      {canExclude && (
        <DropdownMenuItem
          onSelect={() => (isExcluded ? onInclude() : onExclude())}
          data-testid={
            isExcluded ? `tplmgmt-include-${templateId}` : `tplmgmt-exclude-${templateId}`
          }
        >
          <ExcludeIcon />
          {isExcluded ? "Include for my tenant" : "Exclude from my tenant"}
        </DropdownMenuItem>
      )}
      {canDeprecate && <DropdownMenuSeparator className="ds-menu__sep" />}
      {canDeprecate && (
        <DropdownMenuItem
          className="ds-menu__item ds-menu__item--danger"
          onSelect={() => onDeprecate()}
        >
          <DeprecateIcon />
          Deprecate
        </DropdownMenuItem>
      )}
    </DropdownMenu>
  );
}

// Row-action glyphs. The kebab is sized inline; menu-item glyphs are sized
// by `.ds-menu__item svg` in ds-primitives.css. Color inherits currentColor
// so the danger item's red carries through.
function KebabIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={16}
      height={16}
      aria-hidden="true"
    >
      <circle cx="12" cy="5" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="12" cy="19" r="1.4" />
    </svg>
  );
}

function TagIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <circle cx="7" cy="7" r="1.2" />
    </svg>
  );
}

function FlagIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <path d="M4 22v-7" />
    </svg>
  );
}

function ExportIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}

function ExcludeIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 3H7a4 4 0 0 0-4 4v10a4 4 0 0 0 4 4h10a4 4 0 0 0 4-4V7a4 4 0 0 0-4-4z" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </svg>
  );
}

function DeprecateIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6l12.8 12.8" />
    </svg>
  );
}
